import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Inbox, X } from 'lucide-react';
import PayrollRequestsSection from '../payroll/PayrollRequestsSection';

interface PayrollTimeRequestsModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  onRequestProcessed?: () => void;
}

/** Slide-over for reviewing pending payroll time requests (shared with control centre). */
export default function PayrollTimeRequestsModal({
  isOpen,
  onClose,
  businessId,
  onRequestProcessed,
}: PayrollTimeRequestsModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) setIsAnimating(false);
  }, [isOpen]);

  if (!isOpen && !isAnimating) return null;

  const handleTransitionEnd = () => {
    if (!isOpen) setIsAnimating(false);
  };

  const handleClose = () => {
    onClose();
    setIsAnimating(true);
  };

  return createPortal(
    <>
      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 z-[100010] ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      <div
        className={`fixed inset-y-0 right-0 z-[100011] w-full sm:w-[480px] md:w-[600px] max-w-full bg-white shadow-2xl transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="bg-white border-b border-gray-100 flex items-center justify-between shrink-0 px-5"
          style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))', paddingBottom: '0.75rem' }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-blue-50">
              <Inbox className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-[17px] font-semibold text-gray-900 leading-tight">Time Requests</h2>
              <p className="text-xs text-gray-500 mt-0.5">Review pending payroll requests</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-500"
            aria-label="Close time requests"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">
          <PayrollRequestsSection
            businessId={businessId}
            activeFilter="pending"
            onRequestProcessed={() => {
              onRequestProcessed?.();
            }}
          />
        </div>
      </div>
    </>,
    document.body
  );
}
